import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { StatusPill } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  isProviderNotConfigured,
  retryUnlessClientError,
} from '../infrastructure/shared.js';
import './dns.css';

interface CfZone {
  id: string;
  name: string;
  status: string;
}

interface DnsRecord {
  id: string;
  type: string;
  name: string;
  content: string;
  ttl: number;
  proxied: boolean;
  priority: number | null;
}

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'NS', 'SRV', 'CAA'] as const;
type RecordType = (typeof RECORD_TYPES)[number];

const PROXYABLE: RecordType[] = ['A', 'AAAA', 'CNAME'];
const PRIORITY_TYPES: RecordType[] = ['MX', 'SRV'];

function isRecordType(value: string): value is RecordType {
  return (RECORD_TYPES as readonly string[]).includes(value);
}

/** "auto" (or empty) → 1; otherwise an integer 1–86400, else null. */
function parseTtl(value: string): number | null {
  const v = value.trim().toLowerCase();
  if (v === '' || v === 'auto') return 1;
  const n = Number(v);
  if (!Number.isInteger(n) || n < 1 || n > 86400) return null;
  return n;
}

export function DnsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const zonesQuery = useQuery<CfZone[]>({
    queryKey: ['dns', 'zones'],
    queryFn: () => apiFetch<CfZone[]>('/dns/zones'),
    retry: retryUnlessClientError,
  });

  const [selectedZoneId, setSelectedZoneId] = useState('');
  const zones = zonesQuery.data ?? [];
  const zoneId = selectedZoneId || (zones[0]?.id ?? '');
  const zone = zones.find((z) => z.id === zoneId);

  const recordsQuery = useQuery<DnsRecord[]>({
    queryKey: ['dns', 'records', zoneId],
    queryFn: () => apiFetch<DnsRecord[]>(`/dns/zones/${zoneId}/records`),
    enabled: zoneId !== '',
    retry: retryUnlessClientError,
  });

  // ── create/edit dialog ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DnsRecord | null>(null);
  const [type, setType] = useState<RecordType>('A');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [ttlText, setTtlText] = useState('auto');
  const [proxied, setProxied] = useState(false);
  const [priorityText, setPriorityText] = useState('10');

  const openCreate = () => {
    setEditing(null);
    setType('A');
    setName('');
    setContent('');
    setTtlText('auto');
    setProxied(false);
    setPriorityText('10');
    setDialogOpen(true);
  };

  const openEdit = (r: DnsRecord) => {
    setEditing(r);
    setType(isRecordType(r.type) ? r.type : 'A');
    setName(r.name);
    setContent(r.content);
    setTtlText(r.ttl === 1 ? 'auto' : String(r.ttl));
    setProxied(r.proxied);
    setPriorityText(r.priority !== null ? String(r.priority) : '10');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  interface RecordBody {
    type: RecordType;
    name: string;
    content: string;
    ttl: number;
    proxied?: boolean;
    priority?: number;
  }

  const buildBody = (): RecordBody | string => {
    const trimmedName = name.trim();
    const trimmedContent = content.trim();
    if (!trimmedName) return 'name is required';
    if (!trimmedContent) return 'content is required';
    const ttl = parseTtl(ttlText);
    if (ttl === null) return 'TTL must be "auto" or a number between 1 and 86400';
    let priority: number | undefined;
    if (PRIORITY_TYPES.includes(type)) {
      const p = Number(priorityText.trim());
      if (!Number.isInteger(p) || p < 0 || p > 65535) {
        return 'priority must be a number between 0 and 65535';
      }
      priority = p;
    }
    return {
      type,
      name: trimmedName,
      content: trimmedContent,
      ttl,
      ...(PROXYABLE.includes(type) ? { proxied } : {}),
      ...(priority !== undefined ? { priority } : {}),
    };
  };

  const saveMut = useMutation({
    mutationFn: ({ recordId, body }: { recordId: string | null; body: RecordBody }) =>
      recordId === null
        ? apiFetch<DnsRecord>(`/dns/zones/${zoneId}/records`, { method: 'POST', body })
        : apiFetch<DnsRecord>(`/dns/zones/${zoneId}/records/${recordId}`, {
            method: 'PUT',
            body,
          }),
    onSuccess: async (saved, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['dns', 'records', zoneId] });
      closeDialog();
      toast.success(
        vars.recordId === null
          ? `Record "${saved.name}" created`
          : `Record "${saved.name}" updated`,
      );
    },
    onError: (err) => toast.error(errMessage(err, 'failed to save DNS record')),
  });

  const submit = () => {
    const body = buildBody();
    if (typeof body === 'string') {
      toast.error(body);
      return;
    }
    saveMut.mutate({ recordId: editing?.id ?? null, body });
  };

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<DnsRecord | null>(null);
  const deleteMut = useMutation({
    mutationFn: (r: DnsRecord) =>
      apiFetch<{ ok: boolean }>(`/dns/zones/${zoneId}/records/${r.id}`, {
        method: 'DELETE',
        body: { confirmName: r.name },
      }),
    onSuccess: async (_res, r) => {
      await queryClient.invalidateQueries({ queryKey: ['dns', 'records', zoneId] });
      toast.success(`Record "${r.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const columns: Column<DnsRecord>[] = [
    { key: 'type', header: 'Type', render: (r) => <span className="badge">{r.type}</span> },
    { key: 'name', header: 'Name', render: (r) => <span className="dns-name-cell">{r.name}</span> },
    {
      key: 'content',
      header: 'Content',
      render: (r) => (
        <span className="mono ui-cell-mute dns-content-cell" title={r.content}>
          {r.content}
        </span>
      ),
    },
    {
      key: 'ttl',
      header: 'TTL',
      render: (r) => <span className="ui-cell-mute">{r.ttl === 1 ? 'auto' : r.ttl}</span>,
    },
    {
      key: 'proxied',
      header: 'Proxy',
      render: (r) =>
        r.proxied ? (
          <StatusPill variant="ok">proxied</StatusPill>
        ) : (
          <StatusPill variant="neutral">DNS only</StatusPill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="ui-cell-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(r)}>
            Edit
          </button>
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(r)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (isProviderNotConfigured(zonesQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>DNS</h1>
            <p className="page-sub">Cloudflare zones and DNS records.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="Cloudflare" message={zonesQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>DNS</h1>
          <p className="page-sub">Cloudflare zones and DNS records.</p>
        </div>
        <button className="btn btn-amber" onClick={openCreate} disabled={zoneId === ''}>
          Add record
        </button>
      </div>

      <div className="dns-zone-bar">
        <label htmlFor="dns-zone">Zone</label>
        <select
          id="dns-zone"
          value={zoneId}
          onChange={(e) => setSelectedZoneId(e.target.value)}
          disabled={zones.length === 0}
        >
          {zones.length === 0 ? (
            <option value="">{zonesQuery.isLoading ? 'Loading…' : 'No zones found'}</option>
          ) : (
            zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name} ({z.status})
              </option>
            ))
          )}
        </select>
      </div>

      <Table
        columns={columns}
        rows={recordsQuery.data ?? []}
        rowKey={(r) => r.id}
        empty={
          zoneId === ''
            ? zonesQuery.isLoading
              ? 'Loading zones…'
              : zonesQuery.isError
                ? errMessage(zonesQuery.error, 'failed to load zones')
                : 'No zones available on this Cloudflare account.'
            : recordsQuery.isLoading
              ? 'Loading…'
              : recordsQuery.isError
                ? errMessage(recordsQuery.error, 'failed to load DNS records')
                : 'No records in this zone yet.'
        }
      />

      {/* create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? `Edit record "${editing.name}"` : `Add record${zone ? ` to ${zone.name}` : ''}`}
        width={520}
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveMut.isPending) submit();
          }}
        >
          <div className="field-grid-2">
            <div className="field">
              <label htmlFor="dns-type">Type</label>
              <select
                id="dns-type"
                className="mono"
                value={type}
                onChange={(e) => {
                  const next = e.target.value;
                  if (isRecordType(next)) setType(next);
                }}
              >
                {RECORD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="dns-ttl">TTL ("auto" or seconds)</label>
              <input
                id="dns-ttl"
                className="mono"
                value={ttlText}
                onChange={(e) => setTtlText(e.target.value)}
                placeholder="auto"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="dns-name">Name</label>
            <input
              id="dns-name"
              className="mono"
              autoFocus
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={zone ? `www.${zone.name}` : 'www.example.com'}
            />
          </div>
          <div className="field">
            <label htmlFor="dns-content">Content</label>
            <input
              id="dns-content"
              className="mono"
              maxLength={2048}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={type === 'A' ? '203.0.113.10' : type === 'CNAME' ? 'target.example.com' : ''}
            />
          </div>
          {PRIORITY_TYPES.includes(type) ? (
            <div className="field">
              <label htmlFor="dns-priority">Priority</label>
              <input
                id="dns-priority"
                className="mono"
                value={priorityText}
                onChange={(e) => setPriorityText(e.target.value)}
                placeholder="10"
              />
            </div>
          ) : null}
          {PROXYABLE.includes(type) ? (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={proxied}
                onChange={(e) => setProxied(e.target.checked)}
              />
              Proxy through Cloudflare
            </label>
          ) : null}
          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={closeDialog}>
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add record'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Delete DNS record"
        targetName={deleteTarget?.name ?? ''}
        description={
          deleteTarget
            ? `${deleteTarget.type} record pointing to ${deleteTarget.content.slice(0, 80)}`
            : ''
        }
        confirmLabel="Delete record"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
